import { Button, Col, GetProps, Input, message, Select, Table, TableColumnsType, TableProps, Typography } from "antd";
import { ChangeEvent, useEffect, useState } from "react";
import { formatTableText } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";
import { EmptyString } from "src/entity/common/unit.constant";
import { FugitiveService } from "src/entity/fugitive/fugitive.constants";
import { Filter, OptionFilter, OptionMaster, VocResult } from "src/entity/fugitive/fugitive.entity";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { getOptions } from "src/services/fugitive.service";
import { companyStore } from "src/stores/company-store";
import { excelStore } from "src/stores/excel-store.interface";
import { fugitiveStore } from "src/stores/fugitive-store";
import { profileStore } from "src/stores/profile-store";
import { settingStore } from "src/stores/setting-store";
import { generateYearArray } from "src/utils/array";
import { formatNumberWithRoundUpDecimal, parseAndFormatScientificNumberWithoutZeroReturn } from "src/utils/process-number";
import { useShallow } from "zustand/react/shallow";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];
const { Text } = Typography;
const { Search } = Input;
type SearchProps = GetProps<typeof Input.Search>;

const VocResultTable = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));
  const { permissions } = useNavigationMenu();
  const [companyId, setCompanyId] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [current, setCurrent] = useState(1);
  const pageSize = 10;

  const [filter, setFilter] = useState<Filters>({});
  const [optionsFilter, setOptionFilter] = useState<OptionFilter[]>([]);
  const [textSearch, setTextSearch] = useState<string>("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const emission = settingStore(useShallow((state) => state.emission));
  const [emissionMaster, setEmissionMaster] = useState<OptionMaster[] | null>([]);
  const getEmission = settingStore(useShallow((state) => state.getEmission));

  const getAreaByCompanyId = companyStore(useShallow((state) => state.getAreaByCompanyId));
  const areaMaster = companyStore(useShallow((state) => state.dataArea));

  const eqTypeFugitive = fugitiveStore(useShallow((state) => state.eqTypeFugitive));
  const getEqTypeFugitive = fugitiveStore(useShallow((state) => state.getEqTypeFugitive));

  // Excel
  const excelSetAdditional = excelStore(useShallow((state) => state.setAdditional));
  const excelSetQueryStr = excelStore(useShallow((state) => state.setQueryStr));

  // Voc Result Store
  const vocResult = fugitiveStore(useShallow((state) => state.vocResult));
  const totalVocResult = fugitiveStore(useShallow((state) => state.totalVocResult));
  const loadingVocResult = fugitiveStore(useShallow((state) => state.loadingVocResult));
  const getVocResultStore = fugitiveStore(useShallow((state) => state.getVocResult));

  const setSelectedYearVocResult = fugitiveStore(useShallow((state) => state.setSelectedYearVocResult));

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const onChangeYear = (value: number) => {
    setSelectedYear(value);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = (type: "fetch" | "export") => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;
    if (selectedYear) {
      queryParam += `year=${selectedYear}`;
    }
    if (type == "fetch") {
      if (current) {
        const offset = calPagination(current) ?? 0;

        queryParam += `&offset=${offset}`;
      }
      if (limit) {
        queryParam += `&limit=${limit}`;
      }
    }
    if (filter) {
      const toQueryValue = (v: any) => (v === null || v === undefined || v === "" || v === EmptyString ? "__EMPTY__" : v);

      const str = Object.entries(filter)
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => {
          if (Array.isArray(value) && value.length === 0) {
            return `&${key}=[]`;
          }
          if (Array.isArray(value)) {
            const normalized = value.map(toQueryValue);
            return `&${key}=[${normalized.join(",")}]`;
          }
          return "";
        })
        .join("");

      queryParam += str;
    }

    if (textSearch) {
      queryParam += `&textSearch=${textSearch}`;
    }

    return queryParam;
  };

  const fetchOption = async () => {
    try {
      if (selectedYear) {
        setLoading(true);
        const response = await getOptions(selectedYear);
        if (response.data.code === 200) {
          const data = response?.data?.data?.filter;
          if (Array.isArray(data) && data.length > 0) {
            setOptionFilter(data);
          } else {
            setOptionFilter([]);
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVocResult = async () => {
    try {
      if (selectedYear) {
        const offset = calPagination(current) ? calPagination(current) || 0 : 0;

        const filterParams = createParamFilter("fetch");
        const queryString = new URLSearchParams(filterParams).toString();

        await getVocResultStore(queryString);
        excelSetAdditional(totalVocResult);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return text;
    if (type === "area" && areaMaster) {
      const optValue = areaMaster.find((item) => item._id === text);
      return optValue?.areaName;
    } else if (type === "equipmentType") {
      // const optValue = eqTypeFugitive.find(
      //   (item) => item.equipmentType.replaceAll(" ", "").toLowerCase() === text.replaceAll(" ", "").toLowerCase(),
      // );
      const optValue = eqTypeFugitive.find((item) => {
        return item.equipmentTypeList.find((list) => list.replaceAll(" ", "").toLowerCase() === text.replaceAll(" ", "").toLowerCase());
      });
      return optValue?.symbol;
    } else if (type === "service") {
      const optValue = FugitiveService.find(
        (item) =>
          item.value.replaceAll(" ", "").toLowerCase() === text.replaceAll(" ", "").toLowerCase() ||
          item.label.replaceAll(" ", "").toLowerCase() === text.replaceAll(" ", "").toLowerCase(),
      );
      return optValue?.label;
    } else {
      return text;
    }
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = optionsFilter.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        if (key === "causeUncheck") {
          return {
            text: value === EmptyString ? "" : value,
            value: value,
          };
        } else {
          let text = getMasterLabel(key, value) ?? value;
          return {
            text,
            value: value,
          };
        }
      });
      return filter;
    } else {
      return [];
    }
  };

  // const onTableChange: TableProps<VocResult>["onChange"] = (pagination: any, filters: any) => {
  //   let current = pagination.current;
  //   console.log("filters", filters);
  //   setFilter(filters);
  //   setCurrent(current);
  // };

  const onTableChange: TableProps<VocResult>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    // เก็บค่า filter แบบดิบเพื่อให้ filteredValue ตรงกับ value ใน options → เครื่องหมายถูกแสดงถูกต้อง
    setFilter(filters || {});
    setCurrent(current);
  };

  const onSearch: SearchProps["onSearch"] = (value, _e, info) => {
    setTextSearch(value);
    setCurrent(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const sortEmissionPercent = ["bz", "bd", "edc", "vcm"]; // Assuming you want to use the same sorted order
  const columnsEmissionPercent: TableColumnsType<any> =
    emissionMaster
      ?.filter((emission) => emission.value !== "tvoc") // Optional: Exclude "tvoc" if necessary
      .sort((a, b) => {
        const indexA = sortEmissionPercent.indexOf(a.value);
        const indexB = sortEmissionPercent.indexOf(b.value);
        return indexA - indexB; // Sort based on the `sortEmission` order
      })
      .map((emission) => ({
        title: `%${emission.label}`,
        dataIndex: "emissionPercent",
        align: "center",
        render: (value) => {
          const key = emission.value;
          const numberParse = parseAndFormatScientificNumberWithoutZeroReturn(value[key], 2);
          return <Text>{numberParse}</Text>;
        },
      })) || [];

  const sortEmissionKg = ["tvoc", "bz", "bd", "edc", "vcm"];

  const columnsEmissionKg: TableColumnsType<VocResult> =
    emissionMaster
      ?.sort((a, b) => {
        const indexA = sortEmissionKg.indexOf(a.value);
        const indexB = sortEmissionKg.indexOf(b.value);
        return indexA - indexB; // Sort according to the `sortEmission` order
      })
      .filter((emission) => emission.label == "TVOCs")
      .map((emission) => ({
        title: `${emission.label === "TVOC" ? "TVOCs" : emission.label} (Kg/y)`,
        align: "center",
        dataIndex: "emissionKg",
        render: (value) => {
          const key = emission.value;
          // const numberParse = parseAndFormatScientificNumber(value[key], emission.label === "TVOC" ? 8 : 10, 0);
          const numberParse = formatNumberWithRoundUpDecimal(value[key], 4);
          return <Text>{numberParse}</Text>;
        },
      })) || [];

  const columns: TableColumnsType = [
    {
      title: "No.",
      dataIndex: "no",
      render: (_, __, index) => <Text>{index + 1 + (current - 1) * pageSize}</Text>,
    },
    {
      title: "Series",
      dataIndex: "series",
      align: "center",
      filteredValue: filter?.series || null,
      filters: mapFilter("series"),
      render: (text, record) => {
        const series = record?.series;
        const area = record?.areaDetails?.areaName || record.areaStr || text;
        return <Text>{series ?? area}</Text>;
      },
    },
    {
      title: formatTableText("Area"),
      dataIndex: "area",
      align: "center",
      filteredValue: filter?.area || null,
      filters: mapFilter("area"),
      render: (_, record) => {
        let areaName = record?.areaDetails?.areaName || record.areaStr || "";
        return <Text>{areaName}</Text>;
      },
    },
    {
      title: formatTableText("P&ID Number"),
      dataIndex: "pidNumber",
      align: "center",
      filteredValue: filter?.pidNumber || null,
      filters: mapFilter("pidNumber"),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("Equipment ID", ["-"], 9),
      dataIndex: "equipmentID",
      align: "center",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("Equipment Type", ["-"], 9),
      dataIndex: "equipmentType",
      align: "center",
      filteredValue: filter?.equipmentType || null,
      filters: mapFilter("equipmentType"),
      render: (text) => <Text>{getMasterLabel("equipmentType", text)}</Text>,
    },
    {
      title: formatTableText("Substance", ["-"], 9),
      dataIndex: "substance",
      align: "center",
      filteredValue: filter?.substance || null,
      filters: mapFilter("substance"),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("Service"),
      align: "center",
      dataIndex: "service",
      filteredValue: filter?.service || null,
      filters: mapFilter("service"),
      render: (text) => <Text>{getMasterLabel("service", text)}</Text>,
    },
    ...columnsEmissionPercent,
    {
      title: formatTableText("Check Date"),
      align: "center",
      dataIndex: "checkdate",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("VOCs Max (ppm)"),
      align: "center",
      dataIndex: "VOCmax",
      render: (text) => <Text>{parseAndFormatScientificNumberWithoutZeroReturn(text, 2, 0)}</Text>,
    },
    {
      title: formatTableText("Fixed Result (ppm)"),
      align: "center",
      dataIndex: "fixedResult",
      render: (text) => <Text>{text}</Text>,
    },
    ...columnsEmissionKg,
    {
      title: formatTableText("Status"),
      align: "center",
      dataIndex: "statusVOC",
      filteredValue: filter?.statusVOC || null,
      filters: mapFilter("statusVOC"),
      render: (text) => <Text>{text}</Text>,
    },
  ];

  const resetFilter = () => {
    setFilter({});
  };

  useEffect(() => {
    const fetchData = async () => {
      await getEmission({ type: "global", label: "Emission" });
      await getEqTypeFugitive("");
    };
    fetchData();
    fetchYear();
  }, []);

  useEffect(() => {
    if (profile?.activeCompany) {
      const fetchData = async () => {
        const companyId = profile.activeCompany._id;
        if (companyId) {
          setSelectedYear(currentYear);
          setTextSearch("");
          resetFilter();
          setCompanyId(companyId);
        }
      };
      fetchData();
      setCurrent(1);
    }
  }, [profile?.activeCompany]);

  useEffect(() => {
    if (loadingProfile === true) {
      setLoading(true);
    }
  }, [loadingProfile]);

  useEffect(() => {
    if (selectedYear) {
      resetFilter();
      fetchOption();
      setCurrent(1);
      setSelectedYearVocResult(selectedYear);
    }
  }, [selectedYear, companyId]);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedYear) {
        const queryParam = createParamFilter("export");
        if (queryParam) {
          excelSetQueryStr(queryParam);
        }
        await Promise.all([fetchVocResult()]);
      }
    };
    fetchData();
  }, [current, filter, searchTrigger]);

  useEffect(() => {
    if (emission[0] && emission[0].options) {
      setEmissionMaster(emission[0].options);
    }
  }, [emission]);

  return (
    <div>
      {contextHolder}
      <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: 10 }}>
        <Col span={4}>
          <Select
            value={selectedYear}
            style={{ width: 120 }}
            onChange={onChangeYear}
            options={optionYears.map((year) => ({ label: year, value: year }))}
            loading={loading}
            size="middle"
          />
        </Col>

        <Col span={6}>
          <Search onSearch={onSearch} style={{ width: 250 }} value={textSearch} onChange={(e) => setTextSearch(e.target.value)} />
        </Col>

        <Col span={2}></Col>
      </div>
      <Table
        className="w-full whitespace-nowrap"
        columns={columns}
        dataSource={vocResult}
        onChange={onTableChange}
        rowKey="_id"
        scroll={{ x: "max-content" }}
        loading={loading || loadingVocResult}
        pagination={{
          size: "small",
          showSizeChanger: false,
          current,
          pageSize,
          total: totalVocResult,
        }}
      />
    </div>
  );
};
export default VocResultTable;
